import { useState, useEffect } from 'react';
import { Palette, Check, Upload, Monitor, Smartphone, Sun, Moon, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { applyTheme, resetTheme, loadSavedTheme } from '@/lib/theme';

interface ThemePreset {
  id: string;
  name: string;
  description: string;
  colors: { primary: string; secondary: string; accent: string; background: string };
  mode: 'dark' | 'light';
  font: string;
}

const PRESETS: ThemePreset[] = [
  {
    id: 'escuro-moderno',
    name: 'Escuro Moderno',
    description: 'Tema escuro padrão com roxo vibrante',
    colors: { primary: '#10B981', secondary: '#00D2D3', accent: '#00B894', background: '#0F0F14' },
    mode: 'dark',
    font: 'Inter',
  },
  {
    id: 'claro-minimal',
    name: 'Claro Minimal',
    description: 'Tema claro e limpo',
    colors: { primary: '#10B981', secondary: '#00D2D3', accent: '#00B894', background: '#F8F9FC' },
    mode: 'light',
    font: 'Inter',
  },
  {
    id: 'azul-corporativo',
    name: 'Azul Corporativo',
    description: 'Profissional com tons de azul',
    colors: { primary: '#2563EB', secondary: '#0EA5E9', accent: '#06B6D4', background: '#0F172A' },
    mode: 'dark',
    font: 'Geist Sans',
  },
  {
    id: 'verde-natureza',
    name: 'Verde Natureza',
    description: 'Tons verdes naturais',
    colors: { primary: '#10B981', secondary: '#34D399', accent: '#6EE7B7', background: '#0F1F17' },
    mode: 'dark',
    font: 'Inter',
  },
  {
    id: 'roxo-premium',
    name: 'Roxo Premium',
    description: 'Elegante com gradientes roxos',
    colors: { primary: '#8B5CF6', secondary: '#A78BFA', accent: '#C4B5FD', background: '#1E1033' },
    mode: 'dark',
    font: 'Inter',
  },
];

const FONTS = ['Inter', 'Geist Sans', 'Roboto', 'Open Sans', 'Poppins'];

export function ThemePage() {
  const [selectedPreset, setSelectedPreset] = useState('escuro-moderno');
  const [customColors, setCustomColors] = useState({
    primary: '#10B981',
    secondary: '#00D2D3',
    accent: '#00B894',
    background: '#0F0F14',
  });
  const [font, setFont] = useState('Inter');
  const [mode, setMode] = useState<'dark' | 'light'>('dark');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load saved logo on mount
  useEffect(() => {
    const savedLogo = localStorage.getItem('nexopdv_logo');
    if (savedLogo) setLogoPreview(savedLogo);
  }, []);

  const handlePresetSelect = (preset: ThemePreset) => {
    setSelectedPreset(preset.id);
    setCustomColors(preset.colors);
    setFont(preset.font);
    setMode(preset.mode);
    // Apply immediately
    applyTheme({ colors: preset.colors, mode: preset.mode, font: preset.font });
    toast.success(`Tema "${preset.name}" aplicado!`);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Arquivo muito grande. Máximo 2MB.');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Formato inválido. Use PNG, JPG ou SVG.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setLogoPreview(dataUrl);
      // Save logo to localStorage immediately
      localStorage.setItem('nexopdv_logo', dataUrl);
      toast.success('Logo aplicada!');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    applyTheme({ colors: customColors, mode, font });
    if (logoPreview) {
      localStorage.setItem('nexopdv_logo', logoPreview);
    }
    toast.success('Tema salvo e aplicado!');
    setTimeout(() => setSaving(false), 300);
  };

  // Apply theme on color picker change
  const handleColorChange = (key: keyof typeof customColors, value: string) => {
    const newColors = { ...customColors, [key]: value };
    setCustomColors(newColors);
    applyTheme({ colors: newColors, mode, font });
  };

  const handleModeChange = (newMode: 'dark' | 'light') => {
    setMode(newMode);
    applyTheme({ colors: customColors, mode: newMode, font });
  };

  const handleFontChange = (newFont: string) => {
    setFont(newFont);
    applyTheme({ colors: customColors, mode, font: newFont });
  };

  const handleReset = () => {
    resetTheme();
    localStorage.removeItem('nexopdv_logo');
    setLogoPreview(null);
    setCustomColors({ primary: '#10B981', secondary: '#00D2D3', accent: '#00B894', background: '#0F0F14' });
    setMode('dark');
    setFont('Inter');
    setSelectedPreset('escuro-moderno');
    toast.success('Tema resetado para o padrão');
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Personalização de Tema</h1>
        <p className="text-sm text-muted-foreground">
          Customize a aparência do sistema para seu estabelecimento
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Presets */}
          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
            <h3 className="mb-4 text-sm font-medium text-foreground">Temas Pré-definidos</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset)}
                  className={`relative rounded-lg border p-3 text-left transition-all ${
                    selectedPreset === preset.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {selectedPreset === preset.id && (
                    <div className="absolute top-2 right-2">
                      <Check className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div className="flex gap-1 mb-2">
                    {Object.values(preset.colors).map((color, i) => (
                      <div
                        key={i}
                        className="h-4 w-4 rounded-full border border-border"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <p className="text-sm font-medium text-foreground">{preset.name}</p>
                  <p className="text-xs text-muted-foreground">{preset.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Color Pickers */}
          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
            <h3 className="mb-4 text-sm font-medium text-foreground">Cores Personalizadas</h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {(
                [
                  { key: 'primary', label: 'Primária' },
                  { key: 'secondary', label: 'Secundária' },
                  { key: 'accent', label: 'Destaque' },
                  { key: 'background', label: 'Fundo' },
                ] as const
              ).map(({ key, label }) => (
                <div key={key}>
                  <label className="text-xs text-muted-foreground">{label}</label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={customColors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="h-8 w-8 cursor-pointer rounded border border-border"
                    />
                    <input
                      type="text"
                      value={customColors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-full rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Font & Mode */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
              <h3 className="mb-3 text-sm font-medium text-foreground">Fonte</h3>
              <select
                value={font}
                onChange={(e) => handleFontChange(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              >
                {FONTS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
              <h3 className="mb-3 text-sm font-medium text-foreground">Modo</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleModeChange('dark')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    mode === 'dark' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                  }`}
                >
                  <Moon className="h-4 w-4" />
                  Escuro
                </button>
                <button
                  onClick={() => handleModeChange('light')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    mode === 'light' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                  }`}
                >
                  <Sun className="h-4 w-4" />
                  Claro
                </button>
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
            <h3 className="mb-3 text-sm font-medium text-foreground">Logo</h3>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="h-16 w-16 rounded-lg object-contain border border-border"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border">
                  <Palette className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-accent/50">
                  <Upload className="h-4 w-4" />
                  Enviar Logo
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
                <p className="mt-1 text-xs text-muted-foreground">PNG, JPG ou SVG. Máx 2MB.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent/50 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Resetar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Tema'}
            </button>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
            <h3 className="mb-4 text-sm font-medium text-foreground">Preview</h3>

            {/* Desktop Preview */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Monitor className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Desktop</span>
              </div>
              <div
                className="rounded-lg border border-border overflow-hidden"
                style={{ backgroundColor: customColors.background }}
              >
                <div className="h-6 flex items-center px-2 border-b border-border/50">
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="h-4 w-4 rounded" />
                  ) : (
                    <div className="h-3 w-12 rounded" style={{ backgroundColor: customColors.primary }} />
                  )}
                </div>
                <div className="flex">
                  <div className="w-12 border-r border-border/50 p-1 space-y-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-2 rounded"
                        style={{
                          backgroundColor: i === 1 ? customColors.primary : `${customColors.primary}33`,
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex-1 p-2 space-y-2">
                    <div className="grid grid-cols-3 gap-1">
                      {[customColors.primary, customColors.secondary, customColors.accent].map(
                        (c, i) => (
                          <div key={i} className="h-6 rounded" style={{ backgroundColor: `${c}22`, border: `1px solid ${c}44` }}>
                            <div className="h-2 w-6 mt-1 mx-auto rounded" style={{ backgroundColor: c }} />
                          </div>
                        ),
                      )}
                    </div>
                    <div className="h-12 rounded" style={{ backgroundColor: `${customColors.primary}11`, border: `1px solid ${customColors.primary}22` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Preview */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Mobile</span>
              </div>
              <div
                className="mx-auto w-32 rounded-lg border border-border overflow-hidden"
                style={{ backgroundColor: customColors.background }}
              >
                <div className="h-4 flex items-center justify-center border-b border-border/50">
                  <div className="h-1.5 w-8 rounded" style={{ backgroundColor: customColors.primary }} />
                </div>
                <div className="p-1.5 space-y-1">
                  <div className="h-8 rounded" style={{ backgroundColor: `${customColors.primary}22`, border: `1px solid ${customColors.primary}33` }} />
                  <div className="grid grid-cols-2 gap-1">
                    <div className="h-5 rounded" style={{ backgroundColor: `${customColors.secondary}22` }} />
                    <div className="h-5 rounded" style={{ backgroundColor: `${customColors.accent}22` }} />
                  </div>
                </div>
                <div className="h-4 flex items-center justify-around border-t border-border/50 px-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: i === 1 ? customColors.primary : `${customColors.primary}44` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CSS Variables Info */}
          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-5">
            <h3 className="mb-2 text-sm font-medium text-foreground">Variáveis CSS</h3>
            <p className="text-xs text-muted-foreground mb-3">
              O tema é aplicado via CSS variables em tempo real
            </p>
            <div className="space-y-1 text-xs font-mono text-muted-foreground">
              <p>--primary: {customColors.primary}</p>
              <p>--secondary: {customColors.secondary}</p>
              <p>--accent: {customColors.accent}</p>
              <p>--background: {customColors.background}</p>
              <p>--font: {font}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
